import type { FC, ReactNode } from 'react'
import type { Faq } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Container } from '@/components/generals/container'
import { RevealText } from '@/components/generals/reveal-text'
import { FaqList } from '@/components/sections/faq-list'

interface FaqSectionProps {
    faqs: Array<Faq>
    title?: string
    intro?: string
    className?: string
}

export const FaqSection: FC<FaqSectionProps> = ({
    faqs,
    title = 'Questions, answered.',
    intro = 'Security, integrations, onboarding and everything in between. If you do not see your question here, our team is happy to help.',
    className
}): ReactNode => {
    if (!faqs.length) return null

    return (
        <section id='faq' aria-labelledby='faq-title' className={className ?? 'bg-cream'}>
            <Container className='py-28 md:py-40'>
                <div className='grid gap-14 lg:grid-cols-12 lg:gap-10'>
                    <div className='lg:col-span-4'>
                        <div className='lg:sticky lg:top-28'>
                            <Eyebrow>FAQ</Eyebrow>

                            <RevealText
                                as='h2'
                                id='faq-title'
                                text={title}
                                className='mt-6 font-heading text-display-lg font-medium text-burgundy'
                            />

                            <p data-reveal='' className='mt-6 max-w-sm leading-relaxed text-stone-dark'>{intro}</p>

                            <Link
                                href='/contact'
                                className='group mt-8 inline-flex items-center gap-2 border-b border-burgundy/30 pb-1 text-sm font-medium text-burgundy transition-colors duration-300 hover:border-burgundy'
                            >
                                Talk to our team
                                <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:translate-x-0.5' />
                            </Link>
                        </div>
                    </div>

                    <div className='lg:col-span-7 lg:col-start-6'>
                        <FaqList faqs={faqs} />
                    </div>
                </div>
            </Container>
        </section>
    )
}
